// File: components/BottomNavigation.jsx
import {
  Home,
  BookOpen,
  Briefcase,
  FileText,
  MessageCircle,
  NotebookText,
  ListChecks,
  Bell,
} from "@assets/icons/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import React from "react";
import { cn } from "@/lib/utils";

/**
 * Mobile Bottom Navigation: Expanding Pill Design.
 * The active item expands to show a label with a solid background.
 * Inactive items maximize space by showing only icons.
 * Scrollable container to support multiple items.
 */
export function BottomNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navItems = [
    { id: "home", icon: Home, label: "Home", path: "/" },
    { id: "courses", icon: BookOpen, label: "Courses", path: "/courses" },
    { id: "cheatsheets", icon: NotebookText, label: "Cheatsheets", path: "/cheat-sheets" },
    { id: "mcqs", icon: ListChecks, label: "MCQs", path: "/mcqs" },
    { id: "jobs", icon: Briefcase, label: "Jobs", path: "/jobs" },
    { id: "updates", icon: Bell, label: "Updates", path: "/updates" },
    { id: "resume", icon: FileText, label: "Resume", path: "/resume" },
    {
      id: "discussions",
      icon: MessageCircle,
      label: "Discovery",
      path: "/discussions",
    },
  ];

  const isItemActive = (itemPath) => {
    if (itemPath === "/") return pathname === "/";
    return pathname === itemPath || pathname.startsWith(itemPath + "/");
  };

  // Only hide bottom nav if we are in a specific discussion chat (discussionId exists)
  // If we are just on the topics list (/discussions without ID), show the nav (on mobile)
  const [searchParams] = useSearchParams();
  const isDiscussionChat = pathname.includes('/discussions') && !!searchParams.get('discussionId');

  if (isDiscussionChat) return null;

  return (
    <nav className="fixed bottom-3 left-0 right-0 z-50 flex justify-center px-2 md:hidden">
      {/* Container: Glassmorphic Capsule with Horizontal Scroll 
          - scrollbar-hide: standard utility or arbitrary variant
          - gap-3: optimized for showing ~5 items on standard mobile widths (360-390px)
      */}
      <div className="flex h-16 w-full max-w-[98%] items-center gap-2 overflow-x-auto rounded-2xl bg-white/95 px-3 shadow-2xl backdrop-blur-xl ring-1 ring-black/5 dark:bg-gray-900/95 dark:ring-white/10 sm:max-w-md sm:justify-between sm:overflow-visible [&::-webkit-scrollbar]:hidden">
        {navItems.map(({ id, icon: Icon, label, path }) => {
          const active = isItemActive(path);

          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              className={cn(
                "group flex flex-shrink-0 items-center justify-center rounded-xl transition-all duration-500 ease-out",
                active
                  ? "bg-primary/10 py-2.5 px-4"
                  : "bg-transparent p-2.5 text-muted-foreground hover:bg-muted/50"
              )}
              aria-label={label}
              aria-current={active ? "page" : undefined}
            >
              <Icon
                size={22}
                className={cn(
                  "transition-colors duration-300",
                  active ? "text-primary stroke-[2.5px]" : "stroke-2"
                )}
              />

              {/* Label: Slides in horizontally when active */}
              <span
                className={cn(
                  "overflow-hidden whitespace-nowrap font-semibold transition-all duration-500 ease-out",
                  active
                    ? "ml-2 max-w-[120px] opacity-100 text-primary"
                    : "max-w-0 opacity-0"
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
